/**
 * The number on a documents-list tab (Needs review, Teach), held steady around a confirmation
 * (owner, 2026-10-10: 50 waiting, confirm one, and the tab said 83, then 65, then 49).
 *
 * The count a tab is drawn from is right at every instant it is read, but several paths land in
 * it at different moments after a confirmation (the confirmed document, the type recheck, the
 * learning catch-up, the held-work re-judge, the index reconcile), and a pass can raise flags it
 * lowers again a moment later. The person only ever caused the count to go DOWN, so:
 *
 * - A lower count is taken at once, and the number ticks down to it a step at a time (50, 49 …
 *   41), quickly; with reduced motion it lands at once.
 * - After a confirmation, a higher count is never shown while it is still moving. It is shown
 *   only once it has held still for a quiet window with no pass of the person's running, and is
 *   still above what the tab shows. A spike that settles lower is never drawn and says nothing.
 *   Outside that window a higher count (new pages read) shows at once.
 * - A rise that survives settling is real (a document pulled back into review): the number goes
 *   up to it and `roseBy` says by how much, once, so the screen can say why in a small note.
 */

export type SteadyCount = {
  /** The number drawn on the tab. */
  shown: number;
  /** Where `shown` is ticking down to (never above `shown`). */
  target: number;
  /** The newest count seen, and when it last changed. */
  latest: number;
  latestAt: number;
  /** When `shown` last stepped, and by how much each step goes (set when the target moves). */
  steppedAt: number;
  stride: number;
  /** Since the person last confirmed, until things settle: only then is a higher count held back. */
  holding: boolean;
  confirmedAt: number;
  /** Grows each time the number starts down from rest (the tab replays its small tick once per descent). */
  descents: number;
  /** The last settled rise, when, and a serial that grows with each one. */
  roseBy: number;
  roseAt: number;
  roseSerial: number;
  /** Which way the number last moved: the tab's tick goes the same way. */
  lastMove: 'down' | 'up' | null;
};

/** How long a higher count must hold still, with nothing of the person's running, before it shows. */
export const STEADY_COUNT_QUIET_MS = 8_000;
/** The least time after a confirmation that a higher count is held back (the re-judge that can pull
 *  documents back runs after 1.5 to 6 seconds of quiet, and is polled every 4). */
export const STEADY_COUNT_WINDOW_MS = 20_000;
/** One tick of the number on its way down. */
export const STEADY_COUNT_TICK_MS = 60;
/** The most ticks a drop takes, so a drop of 40 is as quick as a drop of 8. */
const MOST_TICKS = 10;

export function steadyCountStart(count: number, now: number): SteadyCount {
  return {
    shown: count,
    target: count,
    latest: count,
    latestAt: now,
    steppedAt: now,
    stride: 1,
    holding: false,
    confirmedAt: Number.NEGATIVE_INFINITY,
    descents: 0,
    roseBy: 0,
    roseAt: Number.NEGATIVE_INFINITY,
    roseSerial: 0,
    lastMove: null,
  };
}

/** The person confirmed (or skipped) something: hold any higher count until it settles. */
export function steadyCountConfirmed(state: SteadyCount, now: number): SteadyCount {
  return { ...state, holding: true, confirmedAt: now, latestAt: now };
}

/**
 * The count after one look at the live number. `busy` is true while a pass the person started is
 * still landing (a confirmation, the learning it set off): a higher number waits for it to end.
 * `instant` lands drops at once (reduced motion). `explained`: the screen has just been told why
 * documents came back (a pull-back), so a rise outside a confirmation is told too.
 */
export function steadyCountStep(
  state: SteadyCount,
  count: number,
  now: number,
  options: { busy?: boolean; instant?: boolean; quietMs?: number; explained?: boolean } = {},
): SteadyCount {
  const quietMs = options.quietMs ?? STEADY_COUNT_QUIET_MS;
  let next = state;
  if (count !== state.latest) next = { ...next, latest: count, latestAt: now };
  const settled = !options.busy && now - next.latestAt >= quietMs;
  if (next.latest > next.shown && (!next.holding || settled))
    // Higher than drawn: new pages arriving show at once; after a confirmation, only a rise that
    // held still with nothing of the person's landing (a document pulled back) is drawn, and told.
    next = {
      ...next,
      shown: next.latest,
      target: next.latest,
      steppedAt: now,
      ...(next.holding || options.explained
        ? {
            roseBy: next.latest - next.shown,
            roseAt: now,
            roseSerial: next.roseSerial + 1,
            lastMove: 'up' as const,
          }
        : {}),
    };
  else if (next.target !== Math.min(next.shown, next.latest))
    // Lower is taken at once; higher (still moving) only stops the descent where it is.
    next = {
      ...next,
      target: Math.min(next.shown, next.latest),
      ...(next.target === next.shown && next.latest < next.shown
        ? { descents: next.descents + 1, lastMove: 'down' as const }
        : {}),
      stride: Math.max(1, Math.ceil((next.shown - Math.min(next.shown, next.latest)) / MOST_TICKS)),
    };
  if (
    next.holding &&
    settled &&
    next.latest <= next.shown &&
    now - next.confirmedAt >= STEADY_COUNT_WINDOW_MS
  )
    next = { ...next, holding: false };
  if (next.shown > next.target) {
    if (options.instant) next = { ...next, shown: next.target, steppedAt: now };
    else if (now - next.steppedAt >= STEADY_COUNT_TICK_MS) {
      next = { ...next, shown: Math.max(next.target, next.shown - next.stride), steppedAt: now };
    }
  }
  return next;
}

/** Whether the number is still on its way somewhere (the caller looks again after a tick). */
export const steadyCountMoving = (state: SteadyCount) =>
  state.shown !== state.target || state.holding;
